import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Trash2, GripVertical, Eye, EyeOff, FolderTree, ImageIcon, Loader2 } from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';
import { formatApiError } from '../lib/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopCollectionsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isLV = language === 'lv';

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [imageBusy, setImageBusy] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    load();
  }, [user]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const uid = user._id || user.id;
      const { data } = await axios.get(`${API}/shops/${uid}/collections`);
      setCollections(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const createCollection = async (e) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      await axios.post(`${API}/shop-collections`, {
        name: draft.name.trim(),
        description: draft.description.trim(),
      });
      setDraft({ name: '', description: '' });
      await load();
    } catch (err) {
      setError(formatApiError(err, isLV ? 'Kļūda' : 'Error'));
    } finally {
      setCreating(false);
    }
  };

  const deleteCollection = async (id) => {
    if (!window.confirm(isLV ? 'Dzēst šo kolekciju? Sludinājumi paliks, tikai zaudēs piederību šai kolekcijai.' : 'Delete this collection? Listings remain but lose this collection membership.')) return;
    try {
      await axios.delete(`${API}/shop-collections/${id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e, isLV ? 'Kļūda' : 'Error'));
    }
  };

  const toggleVisibility = async (coll) => {
    try {
      await axios.put(`${API}/shop-collections/${coll.id}`, { is_public: !coll.is_public });
      await load();
    } catch (e) {
      alert(formatApiError(e, isLV ? 'Kļūda' : 'Error'));
    }
  };

  const startEdit = (coll) => {
    setEditingId(coll.id);
    setEditDraft({ name: coll.name, description: coll.description || '' });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/shop-collections/${id}`, editDraft);
      setEditingId(null);
      await load();
    } catch (e) {
      alert(formatApiError(e, isLV ? 'Kļūda' : 'Error'));
    }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    setImageBusy(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = up.data?.url || up.data?.file_url;
      await axios.put(`${API}/shop-collections/${id}`, { image_url: url });
      await load();
    } catch (e) {
      alert(formatApiError(e, isLV ? 'Augšupielāde neizdevās' : 'Upload failed'));
    } finally {
      setImageBusy(null);
    }
  };

  const move = async (id, direction) => {
    // direction: -1 (up), +1 (down)
    const sorted = [...collections].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex(c => c.id === id);
    const next = idx + direction;
    if (next < 0 || next >= sorted.length) return;
    [sorted[idx], sorted[next]] = [sorted[next], sorted[idx]];
    const order = sorted.map((c, i) => ({ id: c.id, display_order: i }));
    try {
      await axios.post(`${API}/shop-collections/reorder`, { order });
      await load();
    } catch (e) {
      alert(formatApiError(e, isLV ? 'Kļūda' : 'Error'));
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#D97736]" /></div>;
  }

  const sorted = [...collections].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="shop-collections-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/shop-profile" className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6" data-testid="back-to-profile">
          <ArrowLeft className="w-4 h-4" />
          {isLV ? 'Atpakaļ uz veikala profilu' : 'Back to shop profile'}
        </Link>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#D97736]/10 flex items-center justify-center flex-shrink-0">
            <FolderTree className="w-7 h-7 text-[#D97736]" />
          </div>
          <div>
            <h1 className="font-outfit text-3xl font-bold text-[#121212]">
              {isLV ? 'Mana veikala kolekcijas' : 'My shop collections'}
            </h1>
            <p className="text-[#5C5C5C] mt-1 text-sm max-w-2xl">
              {isLV
                ? 'Grupē savas preces vai pakalpojumus tematiski (piem., "Lapenes", "Pirtis", "Dēļi"). Veikala lapā pircēji redzēs šīs kolekcijas kā lielas kartiņas un varēs filtrēt.'
                : 'Group your products / services by theme (e.g., "Gazebos", "Saunas", "Boards"). Buyers see these on your shop page as hero cards and can filter.'}
            </p>
          </div>
        </div>

        {/* Create form */}
        <form onSubmit={createCollection} className="bg-white border border-[#E5E4E0] rounded-2xl p-5 mb-6 space-y-3" data-testid="create-collection-form">
          <h3 className="font-semibold text-[#121212]">{isLV ? 'Pievienot kolekciju' : 'Add collection'}</h3>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
          <div>
            <Label>{isLV ? 'Nosaukums' : 'Name'} *</Label>
            <Input
              value={draft.name}
              onChange={e => setDraft(p => ({ ...p, name: e.target.value }))}
              required
              minLength={2}
              maxLength={60}
              placeholder={isLV ? 'piem., Lapenes' : 'e.g., Gazebos'}
              className="bg-[#FAFAFA] border-[#E5E4E0] mt-1"
              data-testid="collection-name-input"
            />
          </div>
          <div>
            <Label>{isLV ? 'Apraksts (neobligāts)' : 'Description (optional)'}</Label>
            <Textarea
              value={draft.description}
              onChange={e => setDraft(p => ({ ...p, description: e.target.value }))}
              rows={2}
              maxLength={300}
              className="bg-[#FAFAFA] border-[#E5E4E0] mt-1 resize-none"
              data-testid="collection-description-input"
            />
          </div>
          <Button
            type="submit"
            disabled={creating || draft.name.trim().length < 2}
            className="bg-[#D97736] hover:bg-[#C06528] text-white"
            data-testid="create-collection-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            {creating ? (isLV ? 'Veido...' : 'Creating...') : (isLV ? 'Pievienot' : 'Add')}
          </Button>
          <p className="text-xs text-[#8A8A8A]">
            {isLV ? `Šobrīd ${sorted.length}/20 kolekcijas` : `Currently ${sorted.length}/20 collections`}
          </p>
        </form>

        {/* List */}
        {sorted.length === 0 ? (
          <div className="text-center bg-white border border-[#E5E4E0] rounded-2xl p-12">
            <FolderTree className="w-12 h-12 text-[#D97736]/40 mx-auto mb-3" />
            <p className="text-[#5C5C5C]">
              {isLV ? 'Vēl nav nevienas kolekcijas. Izveido pirmo augšā.' : 'No collections yet. Create your first one above.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map((coll, idx) => (
              <div key={coll.id} className="bg-white border border-[#E5E4E0] rounded-2xl p-4" data-testid={`collection-row-${coll.id}`}>
                <div className="flex items-start gap-3">
                  {/* Order arrows */}
                  <div className="flex flex-col gap-1 mt-1">
                    <button
                      onClick={() => move(coll.id, -1)}
                      disabled={idx === 0}
                      className="text-xs text-[#8A8A8A] hover:text-[#D97736] disabled:opacity-30"
                      aria-label="Move up"
                      data-testid={`move-up-${coll.id}`}
                    >▲</button>
                    <button
                      onClick={() => move(coll.id, 1)}
                      disabled={idx === sorted.length - 1}
                      className="text-xs text-[#8A8A8A] hover:text-[#D97736] disabled:opacity-30"
                      aria-label="Move down"
                      data-testid={`move-down-${coll.id}`}
                    >▼</button>
                  </div>

                  {/* Image */}
                  <label className="w-20 h-20 rounded-xl bg-[#FAFAFA] border border-[#E5E4E0] flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#D97736] flex-shrink-0">
                    {imageBusy === coll.id ? (
                      <Loader2 className="w-5 h-5 animate-spin text-[#D97736]" />
                    ) : coll.image_url ? (
                      <img src={resolveAssetUrl(coll.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-[#8A8A8A]" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => uploadImage(coll.id, e.target.files?.[0])}
                      data-testid={`collection-image-${coll.id}`}
                    />
                  </label>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {editingId === coll.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editDraft.name}
                          onChange={e => setEditDraft(p => ({ ...p, name: e.target.value }))}
                          className="bg-[#FAFAFA] border-[#E5E4E0]"
                        />
                        <Textarea
                          value={editDraft.description}
                          onChange={e => setEditDraft(p => ({ ...p, description: e.target.value }))}
                          rows={2}
                          className="bg-[#FAFAFA] border-[#E5E4E0] resize-none"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveEdit(coll.id)} className="bg-[#D97736] hover:bg-[#C06528] text-white">
                            {isLV ? 'Saglabāt' : 'Save'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                            {isLV ? 'Atcelt' : 'Cancel'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-[#121212]">{coll.name}</h3>
                          <span className="text-xs text-[#8A8A8A] bg-[#FAFAFA] px-2 py-0.5 rounded-full">
                            {coll.listing_count} {isLV ? 'sludinājumi' : 'listings'}
                          </span>
                          {!coll.is_public && (
                            <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                              {isLV ? 'Paslēpts' : 'Hidden'}
                            </span>
                          )}
                        </div>
                        {coll.description && (
                          <p className="text-sm text-[#5C5C5C] mt-1 line-clamp-2">{coll.description}</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  {editingId !== coll.id && (
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => startEdit(coll)}
                        className="text-xs px-3 py-1 rounded-full border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736] hover:text-[#D97736]"
                        data-testid={`edit-collection-${coll.id}`}
                      >
                        {isLV ? 'Rediģēt' : 'Edit'}
                      </button>
                      <button
                        onClick={() => toggleVisibility(coll)}
                        className="text-xs px-3 py-1 rounded-full border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736] flex items-center justify-center gap-1"
                        data-testid={`toggle-visibility-${coll.id}`}
                      >
                        {coll.is_public ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      </button>
                      <button
                        onClick={() => deleteCollection(coll.id)}
                        className="text-xs px-3 py-1 rounded-full border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center"
                        data-testid={`delete-collection-${coll.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
